import type { CSSProperties, HTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { Icon } from '../icons/icons.tsx';
import { cx } from '../lib/cx.ts';
import { roleColor } from '../lib/color.ts';
import type { CSSVars } from '../lib/types.ts';
import { useUiT } from '../i18n/index.tsx';

export function Keycap({ keys = [], children }: { keys?: string[]; children?: ReactNode }) {
  const list = children ? [children] : keys;
  return (
    <span className="md-keys">
      {list.map((k, i) => (
        <kbd key={i} className="md-key">
          {k}
        </kbd>
      ))}
    </span>
  );
}

export type BadgeTone = 'neutral' | 'accent' | 'live' | 'bot' | 'success' | 'warning' | 'danger' | 'outline' | 'role';

export interface BadgeProps {
  tone?: BadgeTone;
  icon?: string | null;
  /** role color (token or css color) for tone="role" */
  role?: string | null;
  className?: string;
  title?: string;
  children?: ReactNode;
}

export function Badge({ tone = 'neutral', icon, children, role, className, title }: BadgeProps) {
  if (tone === 'role') {
    return (
      <span
        className={cx('md-badge md-badge--role', className)}
        style={{ '--md-role': roleColor(role, 'var(--role-slate)') } as CSSVars}
        title={title}
      >
        <i aria-hidden="true" />
        {children}
      </span>
    );
  }
  return (
    <span className={cx('md-badge', tone !== 'neutral' && 'md-badge--' + tone, className)} title={title}>
      {icon ? <Icon name={icon} size={12} stroke={2} /> : null}
      {children}
    </span>
  );
}

export function Count({ children, quiet, label }: { children?: ReactNode; quiet?: boolean; label?: string }) {
  return (
    <span className={cx('md-count', quiet && 'md-count--quiet')} aria-label={label}>
      {children}
    </span>
  );
}

export function Verified({ size = 16, label }: { size?: number; label?: string }) {
  const t = useUiT();
  const text = label ?? t('common.verified');
  return (
    <span className="md-verified" title={text} role="img" aria-label={text}>
      <Icon name="verified" size={size} stroke={1.9} />
    </span>
  );
}

export interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  selected?: boolean;
  icon?: string | null;
  /** Shows the × affordance; called when it is activated. */
  onRemove?: (() => void) | boolean;
  /** Non-interactive tag style */
  tag?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode;
}

export function Chip({ selected, icon, onRemove, tag, children, onClick, disabled, className, ...rest }: ChipProps) {
  const interactive = !tag && (onClick || typeof onRemove === 'function');
  const activate = () => {
    if (disabled) return;
    if (onClick) onClick();
    else if (typeof onRemove === 'function') onRemove();
  };
  return (
    <span
      role={interactive ? 'button' : undefined}
      tabIndex={interactive && !disabled ? 0 : undefined}
      aria-pressed={interactive && onClick ? !!selected : undefined}
      aria-disabled={disabled || undefined}
      onClick={interactive ? activate : undefined}
      onKeyDown={
        interactive
          ? (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              } else if ((e.key === 'Backspace' || e.key === 'Delete') && typeof onRemove === 'function') {
                onRemove();
              }
            }
          : undefined
      }
      {...rest}
      className={cx('md-chip', tag && 'md-chip--tag', selected && 'is-selected', className)}
    >
      {icon ? <Icon name={icon} size={14} /> : null}
      {children}
      {onRemove ? <Icon name="close" size={14} className="md-chip-x" /> : null}
    </span>
  );
}

export interface SkeletonProps {
  w?: number | string;
  h?: number | string;
  r?: number | string;
  circle?: boolean;
  style?: CSSProperties;
  className?: string;
}

export function Skeleton({ w = '100%', h = 12, r, circle, style, className }: SkeletonProps) {
  return (
    <span
      className={cx('md-skel', className)}
      aria-hidden="true"
      style={{ display: 'block', width: w, height: h, borderRadius: circle ? '50%' : r, flex: 'none', ...style }}
    />
  );
}

export type BannerTone = 'neutral' | 'warning' | 'danger' | 'accent';

export function Banner({
  tone = 'neutral',
  icon = 'info',
  children,
  action,
  className,
  live = 'polite',
}: {
  tone?: BannerTone;
  icon?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  live?: 'polite' | 'assertive' | 'off';
}) {
  return (
    <div
      className={cx('md-banner', tone !== 'neutral' && 'md-banner--' + tone, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={live}
    >
      <Icon name={icon} size={18} />
      <span style={{ minWidth: 0 }}>{children}</span>
      {action}
    </div>
  );
}

export function Dot({ tone, style }: { tone?: 'online' | 'live'; style?: CSSProperties }) {
  return <span className={cx('md-dot', tone && 'md-dot--' + tone)} style={style} aria-hidden="true" />;
}
